import { lodash } from '@avantia/lodash';
import { hasOwnProperty } from '@avantia/logging-and-errors';
import { HpAnswerMap } from '@avantia/questionset-model';
import { triggerClaimStartedEvent } from '../actions/eventActions';
import { HpClaimsClientConfiguration } from '../config/configurationTypes';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { getAnswersIdCookie, getProductIdCookie } from '../engineClient/tools/cookieUtilities';
import { createVoidEvent } from '../engineClient/tools/utilities';
import { HpClientAnswerMap, HpCustomHashLoadHandler } from '../engineClient/types';
import { getAgentCookie, setAgentMetaDataInAnswersUsingCookie } from './handlerUtilities';
import { HpAllHashParams, HpClearAndAllHashRecord, customerDetailsKeyName } from './handlerTypes';

let productIdDefault = '',
  websiteEndpoint = '',
  rootUrl = '';

registerConfigurationAction((config) => {
  ({ productIdDefault, websiteEndpoint } = config as HpClaimsClientConfiguration);
  rootUrl = `${websiteEndpoint}/`;
});

export const clearAndLoadingHandler: HpCustomHashLoadHandler<HpAllHashParams> = {
  id: 'clearAndLoading',

  canHandleHash: ({ hashMap, location }) => {
    const page = location.pathname;
    return (
      (lodash.keys(hashMap).length === 0 && page === rootUrl) ||
      hasOwnProperty(hashMap, 'clear') ||
      hasOwnProperty(hashMap, 'all')
    );
  },

  needToLoadData: ({ hashMap, search, cookies }) => {
    const record = hashMap as HpClearAndAllHashRecord;
    const hasHashClear = hasOwnProperty(record, 'clear');
    const hasHashAll = !hasHashClear && hasOwnProperty(record, 'all');

    let answersId = getAnswersIdCookie(cookies);
    let productId = getProductIdCookie(cookies);
    if (hasHashAll) {
      productId = undefined;
    }

    if (hasHashClear || hasHashAll) {
      answersId = undefined;
      return {
        load: hasHashAll ? 'all-qsets' : 'qset-only',
        answersId,
        needs: 'generate-id',
        productId: productIdDefault,
        newUrl: rootUrl,
        setCustomAnswers
      };
    }

    const query = new URLSearchParams(search);
    const queryCustomerDetailsKey = query.get(customerDetailsKeyName) || undefined;
    const queryEmailAddress = query.get('emailAddress') || undefined;
    const queryAnswersId = query.get('answersId') || undefined;
    const queryProductId = query.get('productId') || undefined;
    if (queryCustomerDetailsKey || queryEmailAddress) {
      const agentName = getAgentCookie();
      if (triggerClaimStartedEvent && agentName) {
        triggerClaimStartedEvent({
          getEvent: () => createVoidEvent(),
          agentName
        });
      }

      answersId = undefined;
      return {
        load: 'qset-only',
        answersId,
        needs: 'policy-data',
        productId: productIdDefault,
        newUrl: rootUrl,
        setCustomAnswers
      };
    }

    if (queryAnswersId) {
      return {
        load: 'qset+as',
        needs: 'answers',
        answersId: queryAnswersId,
        productId: queryProductId || productIdDefault,
        newUrl: rootUrl,
        setCustomAnswers
      };
    }

    return {
      load: !answersId ? 'qset-only' : 'qset+as',
      needs: answersId ? 'answers' : 'generate-id',
      answersId,
      productId: productId || productIdDefault,
      newUrl: rootUrl,
      setCustomAnswers
    };
  }
};

function setCustomAnswers({ answers }: { answers: HpAnswerMap }): HpClientAnswerMap {
  setAgentMetaDataInAnswersUsingCookie(answers);
  return answers;
}
